"use client"

import { useState } from "react"
import { useDispatch } from "react-redux"
import {
    Button,
    Text,
    VStack,
    Box,
    Heading,
    useToast,
    Flex,
    Icon,
    ScaleFade,
    Container,
    Center,
    keyframes,
    Divider,
    useColorModeValue,
    Badge,
} from "@chakra-ui/react"
import { CheckCircleIcon, EmailIcon, WarningIcon } from "@chakra-ui/icons"
import { verifyEmailAsync } from "../Slice/EmailVerification/EmailVerificationSlice"
import { useNavigate } from "react-router-dom"
import bg from "../image/image1.png";

// Animation keyframes
const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(66, 153, 225, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(66, 153, 225, 0); }
`

const floatEmail = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-8px) rotate(-2deg); }
  50% { transform: translateY(0px) rotate(0deg); }
  75% { transform: translateY(-4px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`

const VerifyEmailButton = () => {
    const dispatch = useDispatch()
    const [message, setMessage] = useState("")
    const [isSuccess, setIsSuccess] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()
    const toast = useToast()
    const cardBg = useColorModeValue("white", "gray.800")
    // Theme colors
    const bgColor = useColorModeValue("white", "gray.800")
    const cardBgGradient = useColorModeValue("linear(to-br, white, gray.50)", "linear(to-br, gray.800, gray.900)")
    const primaryColor = useColorModeValue("blue.500", "blue.300")
    const headingColor = useColorModeValue("blue.600", "blue.300")
    const textColor = useColorModeValue("gray.600", "gray.300")
    const iconBgColor = useColorModeValue("blue.50", "blue.900")

    const handleVerifyEmail = async () => {
        setMessage("")
        setIsSuccess(false)
        setIsLoading(true)

        try {
            await dispatch(verifyEmailAsync()).unwrap()
            setIsSuccess(true)
            setMessage("Verification email sent successfully!")

            toast({
                title: "Email Sent!",
                description: "Please check your inbox to complete verification.",
                status: "success",
                duration: 5000,
                isClosable: true,
                position: "top right",
                variant: "solid",
            })
        } catch (error) {
            setIsSuccess(false)
            setMessage(error.message || "Failed to send verification email")

            toast({
                title: "Error",
                description: error.message || "Failed to send verification email",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top",
                variant: "solid",
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="">
            <div className="bg-mainBg p-3">
                <div className="px-2 sm:px-4   sm:py-4 min-h-screen">

                    <div className="  rounded-xl sm:px-28 ">

                        <ScaleFade initialScale={0.9} in={true}>
                            <Box
                                bg={cardBg}
                                p={6}
                                borderRadius="2xl"
                                // boxShadow="2xl"
                                // bgGradient={cardBgGradient}
                                width="100%"
                                // maxWidth="480px"
                                // borderWidth="1px"
                                // borderColor={useColorModeValue("gray.200", "gray.700")}
                                position="relative"
                                overflow="hidden"
                            // _before={{
                            //     content: '""',
                            //     position: "absolute",
                            //     top: 0,
                            //     left: 0,
                            //     width: "100%",
                            //     height: "8px",
                            //     bgGradient: "linear(to-r, blue.400, purple.500, pink.400)",
                            // }}
                            >
                                <VStack align="center">
                                    {/* Email Verification Icon/Logo with animation */}
                                    <Box
                                        p={5}
                                        borderRadius="full"
                                        bg={iconBgColor}
                                        boxShadow="md"
                                        animation={`${floatEmail} 4s infinite ease-in-out`}
                                        position="relative"
                                    >
                                        <Icon as={EmailIcon} w={10} h={10} color={primaryColor} />
                                        <Badge
                                            position="absolute"
                                            top="-5px"
                                            right="-5px"
                                            colorScheme="green"
                                            borderRadius="full"
                                            animation={`${pulse} 2s infinite`}
                                        >
                                            <CheckCircleIcon boxSize={3} />
                                        </Badge>
                                    </Box>

                                    <Heading size="lg" textAlign="center" color={headingColor} fontWeight="semi-bold" letterSpacing="tight">
                                        Verify Your Email
                                    </Heading>

                                    <Divider />

                                    <Box textAlign="center" px={4}>
                                        <Text color={textColor} fontSize="md" lineHeight="tall">
                                            To activate your account and access all features, please verify your email address by clicking the
                                            button below.
                                        </Text>
                                    </Box>

                                    <Button
                                        colorScheme="blue"
                                        size="lg"
                                        height="60px"
                                        width="full"
                                        onClick={handleVerifyEmail}
                                        isLoading={isLoading}
                                        loadingText="Sending verification email..."
                                        leftIcon={<EmailIcon boxSize={5} />}
                                        _hover={{
                                            transform: "translateY(-3px)",
                                            boxShadow: "xl",
                                            bgGradient: "linear(to-r, blue.500, blue.600)",
                                        }}
                                        bgGradient="linear(to-r, blue.400, blue.500)"
                                        transition="all 0.3s"
                                        borderRadius="lg"
                                        fontWeight="bold"
                                        fontSize="md"
                                    >
                                        Send Verification Email
                                    </Button>

                                    {message && (
                                        <ScaleFade initialScale={0.9} in={!!message}>
                                            <Flex
                                                p={4}
                                                borderRadius="lg"
                                                bg={isSuccess ? "green.50" : "red.50"}
                                                borderLeftWidth="4px"
                                                borderLeftColor={isSuccess ? "green.400" : "red.400"}
                                                width="full"
                                                alignItems="center"
                                                boxShadow="md"
                                            >
                                                <Icon
                                                    as={isSuccess ? CheckCircleIcon : WarningIcon}
                                                    color={isSuccess ? "green.500" : "red.500"}
                                                    mr={4}
                                                    boxSize={5}
                                                />
                                                <Text fontSize="md" fontWeight="medium" color={isSuccess ? "green.700" : "red.700"}>
                                                    {message}
                                                </Text>
                                            </Flex>
                                        </ScaleFade>
                                    )}

                                    {isSuccess && (
                                        <ScaleFade initialScale={0.9} in={isSuccess}>
                                            <Box bg="blue.50" p={4} borderRadius="lg" width="full" textAlign="center" boxShadow="sm">
                                                <Text fontSize="sm" color="blue.700">
                                                    Can't find the email? Check your spam folder or contact support if you need assistance.
                                                </Text>
                                            </Box>
                                        </ScaleFade>
                                    )}
                                </VStack>
                            </Box>
                        </ScaleFade>


                    </div>

                </div>
            </div>
        </div>
    )
}

export default VerifyEmailButton

